import React, { useRef, useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import TextTransformation from '../../utils/textTransformation';
import { getCloudinaryThumbnailUrl } from '../../utils/imageUrlTransformation';
import {
  SwipeableDrawer,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CheckCircleRounded, CancelRounded, WarningAmberRounded } from '@mui/icons-material';
import EditProductForm from './EditProductForm';
import ProductPreviewPanel from './ProductPreviewPanel';

const promotionLabels = {
  buy_one_get_one_free: 'Buy 1 Get 1 Free',
  buy_two_get_one_free: 'Buy 2 Get 1 Free',
  buy_one_get_half_price: 'Buy 1 Get Half Price',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ProductCell = ({ product, toTitleCase, onClick }) => {
  const qty = product.available_quantity || 0;
  const isOutOfStock = qty <= 0;
  const price = parseFloat(product.price || 0).toFixed(2);
  // const thumbSrc = getCloudinaryThumbnailUrl(product.image_url, 36, 36);

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: 'pointer',
        padding: '2px 0',
      }}
    >
      {/* <div
        style={{
          width: '36px',
          height: '36px',
          backgroundColor: '#f0f0f0',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {thumbSrc ? (
          <img
            src={thumbSrc}
            alt=''
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : null}
      </div> */}
      <div>
        <div
          style={{
            fontFamily: 'Inter Medium',
            fontWeight: '600',
            fontSize: '13px',
            color: '#1a1a2e',
          }}
        >
          {toTitleCase(product.product_name || '')}
        </div>
        <div style={{ fontSize: '12px', marginTop: '3px', color: '#555' }}>
          <span style={{ color: '#2e7d32', fontWeight: 800 }}>${price}</span>
          <span style={{ color: '#ccc', margin: '0 5px' }}>|</span>
          {isOutOfStock ? (
            <span style={{ color: '#e53935' }}>Out of Stock</span>
          ) : (
            <span>QTY - {qty}</span>
          )}
        </div>
        <div style={{ fontSize: '11px', color: '#9e9e9e', marginTop: '2px' }}>
          SKU : {product.product_sku || 'N/A'}
        </div>
      </div>
    </div>
  );
};

const ActionMenu = ({ product, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <button
        onClick={handleOpen}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '20px',
          color: '#555',
          padding: '4px 8px',
          borderRadius: '4px',
          lineHeight: 1,
          letterSpacing: '1px',
        }}
        title='Actions'
      >
        ⋮
      </button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            boxShadow: '0 2px 10px rgba(0,0,0,0.12)',
            minWidth: '120px',
            borderRadius: '6px',
          },
        }}
      >
        <MenuItem
          onClick={() => {
            onEdit(product);
            handleClose();
          }}
          sx={{ fontSize: '13px', color: '#333', py: '8px' }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDelete(product);
            handleClose();
          }}
          sx={{ fontSize: '13px', color: '#e53935', py: '8px' }}
        >
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const ListProduct = ({
  filteredProducts,
  currentPage,
  itemsPerPage,
  formatProductTypeLabel,
  onPageChange,
  toTitleCase,
  onEdit,
  getProducts,
  onDeleteProduct,
  onRefresh,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editProductClicked, setEditProductClicked] = useState(false);
  const [clickedProductDetail, setClickedProductDetail] = useState({});
  const [orderBy, setOrderBy] = useState(null);
  const [order, setOrder] = useState(null); // 'asc' | 'desc' | null (null = no sort)

  const theme = useTheme()
  const isNarrow = useMediaQuery(theme.breakpoints.down(900))
  const drawerContainerRef = useRef();

  const HandleClick = (product) => {
    setIsDrawerOpen(true);
    setClickedProductDetail(product);
  };

  const HandleEditProduct = (product) => {
    console.log('----product', product);
    setEditProductClicked(true);
    setIsDrawerOpen(true);
    setClickedProductDetail(product);
  };

  const handleDeleteProduct = async (product) => {
    const payload = {
      product_sku: product.product_sku,
      product_name: product.product_name,
    };

    const result = await onDeleteProduct(payload);
    if (result?.hasError) {
      const msgObj = result.message || {};
      const errorMsg =
        msgObj.errors?.[0]?.errorMessage ||
        msgObj.CustomMessage ||
        'Something went wrong while deleting the product.';
      throw new Error(errorMsg);
    }

    if (result?.success === false) {
      const errorMsg =
        result.errors?.[0]?.errorMessage ||
        result.CustomMessage ||
        'Something went wrong while deleting the product.';
      throw new Error(errorMsg);
    }

    return result;
  };

  const OnDelete = (product) => {
    Swal.fire({
      icon: 'warning',
      iconColor: '#e65100',
      title: 'Delete Product?',
      html: `
        <div style="font-size: 14px; color: #555; line-height: 1.6;">
          Do you want to permanently delete
          <b style="color: #1a1a2e;">${product.product_name || 'this product'}</b> ?
          <span style="color: #e53935; font-size: 12px; margin-top: 6px; display: inline-block;">
            ⚠ This action cannot be undone.
          </span>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#e53935',
      cancelButtonColor: '#9e9e9e',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      focusCancel: true,
      customClass: {
        popup: 'swal-delete-popup',
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          await handleDeleteProduct(product);
        } catch (error) {
          const msg =
            error?.message || 'Something went wrong while deleting the product. Please try again.';
          Swal.showValidationMessage(msg);
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          html: `<b>${product.product_name || 'Product'}</b> has been deleted successfully.`,
          confirmButtonColor: '#2e7d32',
          confirmButtonText: 'OK',
          // timer: 3000,
          // timerProgressBar: true,
        }).then(() => {
          if (typeof onRefresh === 'function') onRefresh();
          else if (typeof getProducts === 'function') getProducts();
        });
      }
    });
  };

  const getPromotion = (product) => {
    const keys = Object.keys(product.product_promotion || {});
    const activeKey = keys.find((k) => product.product_promotion[k]);
    if (!activeKey) return <span style={{ color: '#bbb' }}>—</span>;
    return (
      <span style={{ fontSize: '13px', color: '#2e7d32', fontWeight: 600 }}>
        {promotionLabels[activeKey] || TextTransformation(activeKey)}
      </span>
    );
  };

  const getStatusDisplay = (status_type) => {
    if (!status_type) return null;

    const normalized = String(status_type).toLowerCase();

    if (normalized === 'approved') {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: '#2e7d32',
            fontSize: '13px',
            fontFamily: 'Inter Bold',
          }}
        >
          <CheckCircleRounded sx={{ fontSize: 18, color: '#2e7d32' }} />
          Approved
        </span>
      );
    }

    if (normalized === 'rejected' || normalized === 'reject' || normalized.includes('reject')) {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: '#e53935',
            fontSize: '13px',
            fontFamily: 'Inter Medium',
          }}
        >
          <CancelRounded sx={{ fontSize: 18, color: '#e53935' }} />
          {TextTransformation(status_type)}
        </span>
      );
    }

    if (normalized.includes('pending') || normalized.includes('review')) {
      return (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: '#e65100',
            fontSize: '13px',
            fontFamily: 'Inter Medium',
          }}
        >
          <WarningAmberRounded sx={{ fontSize: 18, color: '#e65100' }} />
          {TextTransformation(status_type)}
        </span>
      );
    }

    return (
      <span
        style={{
          color: '#e65100',
          fontSize: '13px',
          fontFamily: 'Inter Medium',
        }}
      >
        {TextTransformation(status_type)}
      </span>
    );
  };

  const headCells = [
    { id: 'sno', label: 'S.No', sortable: false },
    { id: 'product_name', label: 'Product', sortable: true },
    { id: 'product_type', label: 'Menu Type', sortable: true },
    { id: 'product_category', label: 'Category', sortable: true },
    { id: 'promotion', label: 'Promotion', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'action', label: 'Action', sortable: false },
  ];

  const getPromotionSortValue = (product) => {
    const keys = Object.keys(product.product_promotion || {});
    const activeKey = keys.find((k) => product.product_promotion[k]);
    return activeKey ? promotionLabels[activeKey] || activeKey : '';
  };

  const comparator = (a, b) => {
    let aVal, bVal;
    switch (orderBy) {
      case 'product_name':
        aVal = (a.product_name || '').toLowerCase();
        bVal = (b.product_name || '').toLowerCase();
        break;
      case 'product_type':
        aVal = (a.product_type || '').toLowerCase();
        bVal = (b.product_type || '').toLowerCase();
        break;
      case 'product_category':
        aVal = (a.product_category || '').toLowerCase();
        bVal = (b.product_category || '').toLowerCase();
        break;
      case 'promotion':
        aVal = getPromotionSortValue(a).toLowerCase();
        bVal = getPromotionSortValue(b).toLowerCase();
        break;
      case 'status':
        aVal = a.status === true ? 1 : 0;
        bVal = b.status === true ? 1 : 0;
        break;
      default:
        return 0;
    }
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return order === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  };

  const sortedProducts = useMemo(() => {
    const list = filteredProducts || [];
    if (order == null || orderBy == null) return list;
    return [...list].sort(comparator);
  }, [filteredProducts, orderBy, order]);

  if (!filteredProducts || filteredProducts.length === 0) {
    return (
      <div
        style={{
          padding: '60px',
          textAlign: 'center',
          color: '#999',
          fontSize: '14px',
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '4px',
        }}
      >
        No products available.
      </div>
    );
  }

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedProducts.slice(indexOfFirstItem, indexOfLastItem);

  const handleRequestSort = (event, property) => {
    if (orderBy !== property) {
      setOrderBy(property);
      setOrder('asc');
      return;
    }
    if (order === 'asc') {
      setOrder('desc');
      return;
    }
    if (order === 'desc') {
      setOrderBy(null);
      setOrder(null);
    }
  };

  const tableCellHeadSx = {
    padding: '8px 8px',
    fontFamily: 'Inter Bold',
    fontWeight: 700,
    fontSize: '12px',
    color: '#1a1a2e',
    // borderBottom: '2px solid #f0f0f0',
    borderBottom: '2px solid #010101ff',
    borderTop: '2px solid #010101ff',
    // borderRight: '2px solid #010101ff',
    whiteSpace: 'nowrap',
    backgroundColor: '#ebebeb',
  };

  const tableCellBodySx = {
    padding: '6px 8px',
    // borderBottom: '1px solid #f0f0f0',
    border: '2px solid #f0f0f0',
    fontSize: '12px',
    color: '#444',
    verticalAlign: 'middle',
  };

  return (
    <>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          width: '100%',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: 'white',
          border: '1px solid #e0e0e0',
          borderRadius: '6px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        }}
      >
        <Table sx={{ minWidth: 700 }} size='small' aria-label='products data table'>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell
                  key={cell.id}
                  align={cell.id === 'sno' ? 'center' : 'left'}
                  sx={{
                    ...tableCellHeadSx,
                    ...(cell.id === 'sno' ? { textAlign: 'center' } : {}),
                  }}
                >
                  {cell.sortable ? (
                    <TableSortLabel
                      active={orderBy === cell.id}
                      direction={orderBy === cell.id && order ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, cell.id)}
                    >
                      {cell.label}
                    </TableSortLabel>
                  ) : (
                    cell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {currentItems.map((product, index) => (
              <TableRow
                key={product.product_id ?? product.id ?? index}
                sx={{ backgroundColor: index % 2 === 0 ? 'white' : '#fafafa' }}
                hover
              >
                <TableCell
                  sx={{
                    ...tableCellBodySx,
                    width: 60,
                    color: '#999',
                    fontSize: '13px',
                    textAlign: 'center',
                  }}
                >
                  {indexOfFirstItem + index + 1}
                </TableCell>
                <TableCell sx={{ ...tableCellBodySx, minWidth: 180 }}>
                  <ProductCell
                    product={product}
                    toTitleCase={toTitleCase}
                    onClick={() => HandleClick(product)}
                  />
                </TableCell>
                <TableCell sx={tableCellBodySx}>
                  {formatProductTypeLabel(product.product_type || 'N/A')}
                </TableCell>
                <TableCell sx={tableCellBodySx}>
                  {toTitleCase(product.product_category || 'N/A')}
                </TableCell>
                <TableCell
                  sx={{
                    ...tableCellBodySx,
                    maxWidth: 100,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getPromotion(product)}
                </TableCell>
                <TableCell
                  sx={{
                    ...tableCellBodySx,
                    maxWidth: 170,
                    width: 170,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }}
                >
                  {getStatusDisplay(product.status_type)}
                </TableCell>
                <TableCell sx={{ ...tableCellBodySx, textAlign: 'center', width: 60 }}>
                  <ActionMenu product={product} onEdit={HandleEditProduct} onDelete={OnDelete} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Product detail / edit drawer ── */}
      <SwipeableDrawer
        anchor='right'
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setEditProductClicked(false);
        }}
        onOpen={() => setIsDrawerOpen(true)}
        swipeAreaWidth={56}
        disableSwipeToOpen={false}
        ModalProps={{ keepMounted: true }}
        SwipeAreaProps={{ style: { zIndex: 10 } }}
        container={drawerContainerRef.current}
        sx={{
          '& .MuiDrawer-paper': {
            width: isNarrow ? '100%' : 786,
            minWidth: isNarrow ? '100%' : 786
          }
        }}
      >
        {editProductClicked && (
          <EditProductForm
            onRefresh={onRefresh}
            product={clickedProductDetail}
            onClose={() => {
              setIsDrawerOpen(false)
              setEditProductClicked(false)
            }}
            onUpdate={onRefresh}
          />
        )}

        {!editProductClicked && (
          <ProductPreviewPanel
            product={clickedProductDetail}
            onClose={() => setIsDrawerOpen(false)}
            onEdit={() => setEditProductClicked(true)}
          />
        )}
      </SwipeableDrawer>
    </>
  );
};

export default ListProduct;
